'use client';
import React, {
  Dispatch,
  FC,
  SetStateAction,
  useEffect,
  useState,
} from 'react';
import { useSearchParams } from 'next/navigation';
import { usePathname } from '@/navigation';
import {
  TypeMediaTextTitle,
  TypeProduct,
  TypeProductFields,
} from '@/core/generated-types';
import {
  ShopifyProduct,
  ShopifyVariant,
  getProductInventoryStatus,
  ShopifyProductType,
} from '@/lib/shopify';
import { captureProductFieldsEvent, pushEvent } from '@/utils/dataLayer';
import useProductContext from '@/context/ProductContext';
import {
  DynamicComponent,
  HeaderText,
  Select,
  TrustedShopReviews,
} from '@/components';
import { Image } from '@/components/ui/Image';
import { HeaderSize } from '@/components/ui/HeaderText';
import { YotpoReviewStars } from '@/components/ui/YotpoReviewStars';
import { ProductAddOns } from '../ProductAddOns';
import { InventoryStatus, AddButton } from '../InventoryStatus';
import { VariantTiles, VariantDropdown } from './';
import { ProductVariantCopy } from '@/lib/contentful';
import {
  shouldUseYotpoReviews,
  shouldUseTrustedShopsReviews,
} from '@/utils/helpers';
import { API_URLS } from '@/lib/api/config';
import { PRODUCT_EVENT_TYPES } from '@/utils/rudderstack';
import { getDefaultVariant } from '@/utils/shopify';
import { ITEM_ORIGINS } from '@/core/constants';
import { VariantCircles } from './VariantCircles';

export interface ProductAddToCartProps {
  shopifyProduct: ShopifyProduct | null;
  shopifyProductAddOns: { [title: string]: ShopifyProduct | null };
  product: TypeProduct;
  isVariantSwitcherDisplayed: boolean;
}

interface VariantSwitcherInterface {
  selectedVariant: ShopifyVariant;
  variants: ShopifyVariant[];
  cb: Dispatch<SetStateAction<ShopifyVariant>>;
  displayVariantType: 'tile' | 'dropdown' | 'circle';
  productVariantsCopy: ProductVariantCopy | null;
  product: TypeProduct;
}

const VariantSwitcher: FC<VariantSwitcherInterface> = ({
  displayVariantType,
  selectedVariant,
  variants,
  cb,
  productVariantsCopy,
  product,
}) => {
  switch (displayVariantType) {
    case 'dropdown':
      return (
        <VariantDropdown
          name="variant-select"
          id="variant-select"
          variants={variants}
          selectedVariant={selectedVariant}
          cb={cb}
          productVariantsCopy={productVariantsCopy}
        />
      );
    case 'circle':
      return (
        <VariantCircles
          variants={variants}
          selectedVariant={selectedVariant}
          cb={cb}
          productVariantsCopy={productVariantsCopy}
          product={product}
        />
      );
    case 'tile':
    // fallback to default
    default:
      return (
        <VariantTiles
          variants={variants}
          selectedVariant={selectedVariant}
          cb={cb}
          productVariantsCopy={productVariantsCopy}
        />
      );
  }
};

export const handleFormSubmit = (
  e: React.FormEvent<HTMLFormElement>,
  shopifyProduct: ShopifyProduct,
  variant: ShopifyVariant,
  selectedAddOns: SelectedAddOns[],
  shopifyProductAddOns: { [title: string]: ShopifyProduct | null },
  productFields: TypeProductFields,
  url: string,
  defaultQuantity: number,
) => {
  try {
    e.preventDefault();
    const formData = new FormData(e.currentTarget);
    const qtyVal = Number(formData.get('quantity') || defaultQuantity);

    pushEvent('addToCart', { qtyVal, shopifyProduct, variant });
    captureProductFieldsEvent(PRODUCT_EVENT_TYPES.PRODUCT_ADDED, {
      shopifyProduct,
      variant,
      product: productFields,
      quantity: qtyVal,
      url,
    });

    selectedAddOns.forEach((selectedAddOn) => {
      const shopifyProductAddOn =
        shopifyProductAddOns[selectedAddOn.fields.title];
      if (shopifyProductAddOn) {
        pushEvent('addToCart', {
          qtyVal: 1,
          shopifyProduct: shopifyProductAddOn as ShopifyProduct,
          variant: selectedAddOn.variant,
        });
        captureProductFieldsEvent(PRODUCT_EVENT_TYPES.PRODUCT_ADDED, {
          shopifyProduct: shopifyProductAddOn as ShopifyProduct,
          variant: selectedAddOn.variant,
          product: productFields,
          quantity: 1,
          url,
        });
      }
    });

    e.currentTarget.submit();
  } catch (error) {
    console.error('Error tracking ATC event', error);
    e.currentTarget.submit();
  }
};

export interface SelectedAddOns {
  fields: TypeProductFields;
  variant: ShopifyVariant;
}

const ProductAddToCart: FC<ProductAddToCartProps> = ({
  shopifyProduct,
  shopifyProductAddOns,
  product,
  isVariantSwitcherDisplayed,
}) => {
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const variantUrlParams = searchParams?.get('variant');
  const {
    enableYotpoReviewsSummary,
    enableTrustedShopsReviewsSummary,
    productVariantsCopy,
  } = useProductContext();
  const productFields = product.fields;
  const { variants } = shopifyProduct as ShopifyProduct;
  const defaultVariant = getDefaultVariant(productFields.title, variants);
  const [variant, setVariant] = useState<ShopifyVariant>(defaultVariant);
  const [quantity, setQuantity] = useState<string>('1');
  const [selectedAddOns, setSelectedAddOns] = useState<SelectedAddOns[]>([]);
  const qtyVal = 1;

  useEffect(() => {
    if (shopifyProduct) {
      const urlVariant: ShopifyVariant | undefined = variants?.find(
        (variant) =>
          `gid://shopify/ProductVariant/${variantUrlParams ?? ''}` ==
          variant.id,
      );

      if (urlVariant) {
        setVariant(urlVariant);
        pushEvent('productView', {
          qtyVal,
          shopifyProduct,
          variant: urlVariant,
        });
        captureProductFieldsEvent(PRODUCT_EVENT_TYPES.PRODUCT_VIEWED, {
          shopifyProduct,
          variant: urlVariant,
          product: productFields,
          quantity: qtyVal,
          url: pathname || '',
        });
        return;
      }

      // no variant selected, use the default selected variant
      if (variants && variants.length) {
        setVariant(defaultVariant);
        pushEvent('productView', {
          qtyVal: 1,
          shopifyProduct,
          variant: defaultVariant,
        });
        captureProductFieldsEvent(PRODUCT_EVENT_TYPES.PRODUCT_VIEWED, {
          shopifyProduct,
          variant: defaultVariant,
          product: productFields,
          quantity: qtyVal,
          url: pathname || '',
        });
        return;
      }

      console.warn('Product is missing variants');
    }
  }, [
    shopifyProduct,
    variantUrlParams,
    defaultVariant,
    variants,
    productFields,
    pathname,
  ]);

  const inventoryStatus = getProductInventoryStatus(shopifyProduct, variant);

  const isCoursePack =
    shopifyProduct?.productType === ShopifyProductType.COURSE;
  const isSkillSet =
    shopifyProduct?.productType === ShopifyProductType.SKILL_SET;
  const productAddonsWithShopifyData = productFields.productAddOns?.filter(
    (productAddOn) =>
      shopifyProductAddOns.hasOwnProperty(productAddOn.fields.title),
  );

  const selectedAddOnsVariants = selectedAddOns.map((selectedAddOn) => {
    const selectedAddonShopifyProduct =
      shopifyProductAddOns[selectedAddOn.fields.title];

    const selectedVariantId = selectedAddOn?.variant.id;
    const defaultVariantId = selectedAddonShopifyProduct?.variants?.[0].id;
    if (selectedAddonShopifyProduct) {
      return {
        merchandiseId: selectedVariantId || defaultVariantId,
        quantity: 1,
        attributes: [
          // this attribute supports removing standalone addons from the cart when the primary product is removed from the cart
          // i.e for course packs, when the course pack is removed, any standalone addons added with the course pack will also be removed.
          {
            key: 'linked_product_handle',
            value: shopifyProduct?.handle,
          },
          {
            key: 'item_origin',
            value: ITEM_ORIGINS.PRODUCT_PAGE,
          },
        ],
      };
    }
  });

  return (
    <div className="flex flex-col sm:px-6">
      <div className="flex items-center mb-2 md:mb-3">
        {productFields.previousTitle && (
          <div className="mr-2 text-h2 font-bold" data-testid="previous-title">
            {productFields.previousTitle}
          </div>
        )}
        {productFields.badge && (
          <DynamicComponent component={productFields.badge} />
        )}
      </div>
      <h1 className="mb-1.5 text-[32px] md:text-[48px] font-bold text-charcoal-v2 leading-[48px] md:leading-[56px]">
        {productFields.title}
      </h1>
      {productFields.shortDescription && (
        <p className="mb-4" data-testid="product-description">
          {productFields.shortDescription}
        </p>
      )}
      {variant && (
        <p
          className="mb-3 text-[1.3rem] font-bold md:text-2xl"
          data-testid="price"
        >
          {productFields.precedingPriceText && (
            <span data-testid="preceding-price">
              {productFields.precedingPriceText}
            </span>
          )}
          {variant.formattedPrice}
        </p>
      )}
      {shouldUseYotpoReviews() && enableYotpoReviewsSummary && (
        <div className="mb-2 md:mb-3" data-testid="review-summary">
          <YotpoReviewStars product={product} />
        </div>
      )}
      {shouldUseTrustedShopsReviews() && enableTrustedShopsReviewsSummary && (
        <div className="mb-2" data-testid="review-summary">
          <TrustedShopReviews
            shopifyProduct={shopifyProduct}
            widgetType="ratings-short"
          />
        </div>
      )}
      {productAddonsWithShopifyData && (
        <ProductAddOns
          productAddOns={productAddonsWithShopifyData}
          shopifyProductAddOns={shopifyProductAddOns}
          selectedAddOns={selectedAddOns}
          setSelectedAddOns={setSelectedAddOns}
          productFields={productFields}
          defaultVariant={defaultVariant}
        />
      )}
      {isVariantSwitcherDisplayed && (
        <VariantSwitcher
          displayVariantType={product?.fields?.displayVariantType || 'tile'}
          selectedVariant={variant}
          variants={shopifyProduct?.variants as ShopifyVariant[]}
          cb={setVariant}
          productVariantsCopy={productVariantsCopy || null}
          product={product}
        />
      )}
      {shopifyProduct && (
        <form
          id="add-to-cart-form"
          action={`${API_URLS.CART_SERVICE}/add`}
          method="post"
          onSubmit={(e) =>
            handleFormSubmit(
              e,
              shopifyProduct,
              variant,
              selectedAddOns,
              shopifyProductAddOns,
              product.fields,
              pathname || '',
              qtyVal,
            )
          }
        >
          <input
            type="hidden"
            name="lines"
            value={JSON.stringify([
              // add variants before the main product so that the addon product
              // variants are listed below the primary product in the cart
              ...selectedAddOnsVariants,
              {
                merchandiseId: variant.id,
                quantity: Number(quantity),
                attributes: [
                  {
                    key: 'quantity_limited_to_one',
                    value: String(isCoursePack || isSkillSet),
                  },
                  {
                    key: 'is_course_pack',
                    value: String(isCoursePack),
                  },
                  {
                    key: 'is_skill_set',
                    value: String(isSkillSet),
                  },
                  {
                    key: 'item_origin',
                    value: ITEM_ORIGINS.PRODUCT_PAGE,
                  },
                ],
              },
            ])}
          />
          {/* Hides the quantity selector when the product has a radio option selector */}
          {productFields.quantitySelector && (
            <div className="mb-5 md:mb-6 md:max-w-[360px]">
              <Select
                {...productFields.quantitySelector}
                name="quantity"
                onChangeEvent={(e) => setQuantity(e.target.value)}
                labelClassName="text-base"
              />
            </div>
          )}
          <div className="md:max-w-[360px] text-sm mb-2">
            <InventoryStatus
              inventoryStatus={inventoryStatus}
              product={product}
            />
          </div>
          {!isVariantSwitcherDisplayed && productFields.backOrderMessage && (
            <p>{productFields.backOrderMessage}</p>
          )}
          <div className="md:max-w-[360px]">
            <AddButton inventoryStatus={inventoryStatus} product={product} />
          </div>
        </form>
      )}
      <ul className="mt-4">
        {productFields.productValueProps?.map((item) => {
          const {
            sys: { id },
            fields: { textHeading, image },
          } = item as TypeMediaTextTitle;

          return (
            <li
              key={id}
              className="flex-start flex flex-row items-center gap-2"
            >
              {image && (
                <Image imageData={image} height={16} width={16} alt="" />
              )}
              <p className="w-auto">{textHeading}</p>
            </li>
          );
        })}
      </ul>
    </div>
  );
};

export default ProductAddToCart;
